import {
    AutoHeightContainer,
    GlobalReferralsEmptyState,
    PrioritySearch,
    PrioritySearchMatch,
    PriorityViewTable,
    useGetLocationPostalCodesQuery,
    useGetPriorityStatusesQuery,
    usePageSearch
} from "@therapprove/common";
import { useMemo } from "react";
import { GlobalReferralHeader } from "./GlobalReferralHeader";
import {
    buildFilterWhere,
    useGlobalReferralFilters
} from "./useGlobalReferralFilters";

export const GlobalPriorityEntries = () => {
    // State
    const { search, setSearch, debouncedSearch } = usePageSearch();
    const { filters, setFilters } = useGlobalReferralFilters();

    // Queries
    const [{ data: locationPostalCodesData }] =
        useGetLocationPostalCodesQuery({
            requestPolicy: "cache-and-network"
        });
    const [{ data: priorityStatusesData }] = useGetPriorityStatusesQuery({
        requestPolicy: "cache-and-network"
    });

    // Data
    const locationPostalCodes =
        locationPostalCodesData?.locations?.locations
            ?.map((location) => location.postalCode)
            .filter(
                (postalCode) => postalCode !== null && postalCode !== undefined
            ) ?? [];

    const priorityStatuses =
        priorityStatusesData?.priorityStatuses?.priorityStatuses ?? [];
    const archivedStatus = priorityStatuses.find(
        (status) => status.name.toLowerCase() === "archived"
    );

    // Build the combined filter: base global filter + user-selected filters.
    const globalFilter: PrioritySearch = useMemo(() => {
        const userWhere = buildFilterWhere(filters, locationPostalCodes);

        return {
            where: {
                ...userWhere,
                is_global: true,
                // Exclude archived entries from global referrals
                priority_status_id: archivedStatus
                    ? {
                          ids: [archivedStatus.priorityStatusId],
                          match: PrioritySearchMatch.None
                      }
                    : undefined,
                // Default: 35mi proximity. Override: use user's distance selection.
                location: userWhere.location ?? {
                    distance_in_miles: 35,
                    postal_codes: locationPostalCodes
                }
            }
        };
    }, [filters, locationPostalCodes, archivedStatus]);

    return (
        <AutoHeightContainer style={{ gap: "1rem" }}>
            <GlobalReferralHeader
                search={search}
                setSearch={setSearch}
                filters={filters}
                setFilters={setFilters}
            />
            <PriorityViewTable
                search={debouncedSearch}
                priorityStatusList={priorityStatuses}
                isGlobalView={true}
                options={{
                    emptyState: <GlobalReferralsEmptyState />,
                    searchBody: globalFilter
                }}
            />
        </AutoHeightContainer>
    );
};
